import React, { useState, useEffect, useRef } from "react";
import { useMeQuery } from "@/queries/queries";
import { useCompanyESPMdata } from "@/queries/useCompanyQueries";

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/shared/ui/dialog";
import { Check, Loader2Icon } from "lucide-react";
import { toast } from "@/queries/useToast";
import ConnectAccount from "./ConnectAccount";
import ConnectProperty from "./ConnectProperty";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

function usePreviousEspmAccounts(value: any) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current as any[] | undefined;
}

interface PortfolioManagerConnectModalProps {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

type StepNumber = 1 | 2;

interface StepperProps {
  currentStep: StepNumber;
}

const Stepper: React.FC<StepperProps> = ({ currentStep }) => {
  const steps: { n: StepNumber; label: string }[] = [
    { n: 1, label: "Connect account" },
    { n: 2, label: "Share building" },
  ];

  return (
    <div
      className="flex items-center gap-2 mt-3"
      role="list"
      aria-label="Connect to Portfolio Manager steps"
    >
      {steps.map((step, idx) => {
        const isCompleted = currentStep > step.n;
        const isActive = currentStep === step.n;
        return (
          <React.Fragment key={step.n}>
            <div
              role="listitem"
              aria-current={isActive ? "step" : undefined}
              className="flex items-center gap-2"
            >
              <div
                className={cn(
                  "flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium shrink-0 border",
                  isCompleted &&
                    "bg-success text-success-foreground border-success",
                  isActive &&
                    "bg-primary text-primary-foreground border-primary",
                  !isCompleted &&
                    !isActive &&
                    "bg-transparent text-muted-foreground border-border",
                )}
                aria-hidden="true"
              >
                {isCompleted ? <Check className="w-3.5 h-3.5" /> : step.n}
              </div>
              <span
                className={cn(
                  "text-sm",
                  isActive && "font-medium text-foreground",
                  isCompleted && "text-foreground",
                  !isActive && !isCompleted && "text-muted-foreground",
                )}
              >
                {step.n}. {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                className={cn(
                  "flex-1 h-px",
                  currentStep > step.n ? "bg-success" : "bg-border",
                )}
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const PortfolioManagerConnectModal = ({
  open,
  onOpenChange,
}: PortfolioManagerConnectModalProps) => {
  const { data: user, isLoading: isUserLoading } = useMeQuery();
  const { data: espmAccounts, isLoading: isESPMLoading } = useCompanyESPMdata(
    user?.company?.id || "",
    true,
  );
  const queryClient = useQueryClient();
  const activeEspmAccounts = espmAccounts?.filter((a) => a.sharingStatus >= 2);
  const prevActiveEspmAccounts = usePreviousEspmAccounts(
    activeEspmAccounts || [],
  );

  const [currentStep, setCurrentStep] = useState<StepNumber>(
    (espmAccounts?.length || 0) > 0 ? 2 : 1,
  );

  // Keep the initial step in sync once accounts finish loading / when the
  // modal is (re)opened.
  useEffect(() => {
    if (!open) return;
    setCurrentStep((espmAccounts?.length || 0) > 0 ? 2 : 1);
    // Only recompute on open transitions / when account list first resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    let newEspmProperties: any[] = [];
    if (activeEspmAccounts) {
      if (activeEspmAccounts.length > (prevActiveEspmAccounts?.length || 0)) {
        // Find new accounts by filtering out those that were already present
        const newAccounts = activeEspmAccounts.filter(
          (account) =>
            !prevActiveEspmAccounts?.some(
              (prevAccount) => prevAccount.id === account.id,
            ),
        );
        newAccounts.forEach((account) => {
          if (open) {
            toast({
              description: `New Portfolio Manager Account added: ${account.accountOrganization}`,
            });
          }
        });
        if (newAccounts.length > 0) {
          setCurrentStep(2);
        }
      }
      const prevEspmProperties = prevActiveEspmAccounts
        ?.flatMap((a) => a.espmProperties)
        .filter((p) => p.sharingStatus >= 2);
      const currentEspmProperties = activeEspmAccounts
        .flatMap((a) => a.espmProperties)
        .filter((p) => p.sharingStatus >= 2);
      if (currentEspmProperties.length > (prevEspmProperties?.length || 0)) {
        newEspmProperties = currentEspmProperties.filter(
          (property) =>
            !prevEspmProperties?.some(
              (prevProperty) => prevProperty.id === property.id,
            ),
        );
        newEspmProperties.forEach((property) => {
          if (open) {
            toast({
              description: `New property shared: ${property.buildingName || property.address1}`,
            });
          }
        });
        if (newEspmProperties.length > 0) {
          queryClient.invalidateQueries();
          onOpenChange(false);
        }
      }
    }
  }, [espmAccounts]);

  if (open && (isUserLoading || isESPMLoading)) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-full sm:max-w-[680px] max-w-full sm:max-h-[85vh] max-h-[100dvh] sm:rounded-lg rounded-none p-0 flex flex-col gap-0">
          <DialogTitle className="sr-only">
            Connect to Portfolio Manager
          </DialogTitle>
          <div className="flex-1 min-h-0 flex justify-center items-center p-8">
            <Loader2Icon className="animate-spin" size={24} />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const primaryAccount = activeEspmAccounts?.[0];
  const accountName =
    primaryAccount?.accountOrganization &&
    primaryAccount.accountOrganization !== "Unknown"
      ? primaryAccount.accountOrganization
      : `${primaryAccount?.accountFirstName || ""} ${
          primaryAccount?.accountLastName || ""
        }`.trim();
  const accountEmail = primaryAccount?.accountEmail;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-[680px] max-w-full sm:max-h-[85vh] max-h-[100dvh] sm:rounded-lg rounded-none p-0 flex flex-col gap-0">
        <div className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Connect to Portfolio Manager</DialogTitle>
          <Stepper currentStep={currentStep} />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 py-4">
          {currentStep === 1 && <ConnectAccount />}
          {currentStep === 2 && (
            <div className="flex flex-col gap-4">
              {primaryAccount && (
                <div className="flex items-center justify-between gap-2 flex-wrap bg-muted/40 border border-border rounded-md px-3 py-2 text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <Check
                      className="w-4 h-4 text-success shrink-0"
                      aria-hidden="true"
                    />
                    <span className="truncate">
                      Connected:{" "}
                      <span className="font-medium">{accountName}</span>
                      {accountEmail && (
                        <span className="text-muted-foreground">
                          {" "}
                          ({accountEmail})
                        </span>
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="text-sm text-primary hover:underline whitespace-nowrap shrink-0"
                  >
                    Connect a different account
                  </button>
                </div>
              )}
              <ConnectProperty />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PortfolioManagerConnectModal;
